import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Users, BookOpen, Trash2, Crown } from 'lucide-react';
import { useState } from 'react';
import { teamsApi } from '../api/teams';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Textarea from '../components/Textarea';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import UserBadge from '../components/UserBadge';
import { formatRelativeTime } from '../utils/formatRelativeTime';
import { useAuth } from '../contexts/useAuth';

export default function TeamsListPage() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const { data: teams, isLoading, error } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.getAll(),
    refetchInterval: 30000,
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      teamsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setIsModalOpen(false);
      setName('');
      setDescription('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => teamsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
    },
  });

  const handleDelete = (id: string, teamName: string) => {
    if (window.confirm(`Вы уверены, что хотите удалить команду "${teamName}"? Проекты команды станут без привязки к командам.`)) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      name: name.trim(),
      description: description.trim() || undefined,
    });
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Ошибка загрузки команд" />;

  const teamsArray = Array.isArray(teams) ? teams : [];

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Мои команды
        </h1>
        <Button className="w-full sm:w-auto flex items-center justify-center" onClick={() => setIsModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Создать команду
        </Button>
      </div>

      {teamsArray.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              Нет команд
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Создайте команду для совместной работы над проектами
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {teamsArray.map((team) => (
            <Card key={team.id} className="hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Link to={`/teams/${team.id}`} className="block mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors">
                      {team.name}
                    </h3>
                  </Link>
                  {team.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
                      {team.description}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      {team.member_count ?? 0}
                    </div>
                    <div className="flex items-center">
                      <BookOpen className="h-4 w-4 mr-1" />
                      {team.series_count ?? 0}
                    </div>
                    {team.my_role === 'owner' && (
                      <div className="flex items-center text-amber-600 dark:text-amber-400">
                        <Crown className="h-4 w-4 mr-1" />
                        Владелец
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <UserBadge username={team.created_by_username} />
                    {team.created_at && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {formatRelativeTime(team.created_at)}
                      </span>
                    )}
                  </div>
                </div>
                {(team.my_role === 'owner' || isAdmin) && (
                  <div className="ml-2">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDelete(team.id, team.name);
                      }}
                      disabled={deleteMutation.isPending}
                      className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      title="Удалить команду"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Создать новую команду"
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!name.trim() || createMutation.isPending}
            >
              Создать
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Название команды"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Например: Переводы манхвы"
          />
          <Textarea
            label="Описание (опционально)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Кратко опишите цель команды"
            rows={3}
          />
        </form>
      </Modal>
    </div>
  );
}
